import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ConfiguratorContext } from "../../contexts/ConfiguratorContext";
import { AuthContext } from "../../contexts/AuthContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import {
  ADVERT_MESSAGE_KEY,
  HAS_ADVERT_KEY,
} from "../constants/Configurator.constants";
import { PAYMENTS_SUPPORT_ROLE } from "../constants/Roles.constants";
import { parseConfigBoolean } from "../utils/utility-functions";
import LoginAdvertPopup from "../../modules/dashboard/components/LoginAdvertPopup";

const EXCLUDED_PATH_PREFIXES = ["/logout"];

const isAuthDebugEnabled = () => {
  try {
    return window.localStorage.getItem("auth_debug") === "1";
  } catch (_) {
    return false;
  }
};

/**
 * Shows the login advert once per session after config loads and loading overlay clears.
 */
const LoginAdvertSession = () => {
  const location = useLocation();
  const { edittedFields, hasRetrievedConfig } = useContext(ConfiguratorContext);
  const { requestPendingCount } = useContext(ThemeContext);
  const { token, userRoles, userDetails } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const loginAdvertUserKeyRef = useRef(null);
  const hasOpenedThisSessionRef = useRef(false);

  const hasAdvertField = useMemo(
    () => edittedFields.find((item) => item.key === HAS_ADVERT_KEY),
    [edittedFields],
  );
  const advertMessageField = useMemo(
    () => edittedFields.find((item) => item.key === ADVERT_MESSAGE_KEY),
    [edittedFields],
  );

  const advertEnabled = useMemo(() => {
    if (parseConfigBoolean(hasAdvertField?.value)) {
      return true;
    }
    const message = advertMessageField?.value;
    return message != null && String(message).trim() !== "";
  }, [hasAdvertField, advertMessageField]);

  const isAllowedRoute = !EXCLUDED_PATH_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix),
  );

  const canShowAdvert =
    hasRetrievedConfig &&
    Boolean(token) &&
    advertEnabled &&
    isAllowedRoute &&
    !userRoles?.includes(PAYMENTS_SUPPORT_ROLE) &&
    requestPendingCount === 0;

  useEffect(() => {
    const sub =
      userDetails?.idTokenClaims?.sub ?? userDetails?.localAccountId ?? null;
    if (!sub || typeof sub !== "string") {
      return;
    }
    if (loginAdvertUserKeyRef.current !== sub) {
      loginAdvertUserKeyRef.current = sub;
      hasOpenedThisSessionRef.current = false;
      try {
        sessionStorage.setItem("_adv_shown", "false");
      } catch (_) {
        /* ignore */
      }
    }
  }, [userDetails?.idTokenClaims?.sub, userDetails?.localAccountId]);

  useEffect(() => {
    if (!canShowAdvert) {
      return;
    }
    if (hasOpenedThisSessionRef.current) {
      return;
    }
    try {
      if (sessionStorage.getItem("_adv_shown") === "true") {
        return;
      }
    } catch (_) {
      /* ignore */
    }
    hasOpenedThisSessionRef.current = true;
    setOpen(true);
  }, [canShowAdvert]);

  useEffect(() => {
    if (!isAuthDebugEnabled()) {
      return;
    }
    console.info("[login-advert]", {
      _adv_shown: sessionStorage.getItem("_adv_shown"),
      advertEnabled,
      canShowAdvert,
      open,
      pathname: location.pathname,
      hasAdvertRaw: hasAdvertField?.value,
      advertMessage: advertMessageField?.value,
      hasRetrievedConfig,
      hasToken: Boolean(token),
      requestPendingCount,
      userRoles,
    });
  }, [
    advertEnabled,
    canShowAdvert,
    open,
    location.pathname,
    hasAdvertField,
    advertMessageField,
    hasRetrievedConfig,
    token,
    requestPendingCount,
    userRoles,
  ]);

  if (!hasRetrievedConfig || !token || !advertEnabled) {
    return null;
  }

  return <LoginAdvertPopup open={open} setOpen={setOpen} />;
};

export default LoginAdvertSession;
